import { isLpgProcurement } from '../../../../../shared/offer-domain/constants';
import { pick } from '../../../../../shared/offer-domain/pick';
import {
  dedupeNegotiationItems,
  hasNegotiationOfferId,
  isCounterMissingId,
} from '../../../../../shared/offer-domain/negotiation';
import {
  buildLpgRequirementsFromParcel,
  buildRequirementsFromParcel,
  displayValue,
  getRequirementIncotermRouteLabel,
} from '../../../utils/tenderEvaluationHelpers';

export const getParcelId = parcel =>
  parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;

export const getParcelLabel = parcel =>
  displayValue(parcel?.deliveryUnit, 'Delivery Unit');

export const buildRequirementOptions = (parcel, procurement) => {
  const requirements = isLpgProcurement(procurement)
    ? buildLpgRequirementsFromParcel(parcel)
    : buildRequirementsFromParcel(parcel);

  return requirements.map((requirement, requirementIndex) => {
    const deliveryBasis = displayValue(requirement?.deliveryBasis, '-');
    const routeLabel = getRequirementIncotermRouteLabel(requirement);
    return {
      requirement,
      requirementIndex,
      label: `Requirement ${requirementIndex + 1}`,
      subLabel:
        routeLabel !== '-' ? `${deliveryBasis} · ${routeLabel}` : deliveryBasis,
    };
  });
};

export const getUnselectedDeliveryUnits = (
  parcels = [],
  selectionByParcel = {}
) =>
  (parcels ?? [])
    .map(parcel => {
      const parcelId = getParcelId(parcel);
      if (parcelId == null) return null;
      const entry = selectionByParcel[String(parcelId)];
      if (entry?.requirementIndex != null || entry?.locked) return null;
      return {
        parcelId,
        deliveryUnit: getParcelLabel(parcel),
      };
    })
    .filter(Boolean);

/** Cargo label: BE `QuantityDeliveryTerm.DeliveryUnit`, fallback by order. */
export const getCargoLabel = (parcel, cargoIndex = 0) => {
  const unit = parcel?.deliveryUnit;
  return unit != null && String(unit).trim() !== '' && String(unit) !== '-'
    ? String(unit)
    : `Cargo ${String.fromCharCode(65 + cargoIndex)}`;
};

const countableOffers = offers =>
  dedupeNegotiationItems(
    (offers ?? []).filter(offer => hasNegotiationOfferId(offer?.idOffer))
  );

const collectParcelIssues = (parcels, selectionByParcel, pickIssue) =>
  (parcels ?? [])
    .map(parcel => {
      const parcelId = getParcelId(parcel);
      if (parcelId == null) return null;
      const entry = selectionByParcel[String(parcelId)];
      if (entry?.requirementIndex == null && !entry?.locked) return null;
      const detail = pickIssue(countableOffers(entry?.offers));
      return detail
        ? { parcelId, deliveryUnit: getParcelLabel(parcel), ...detail }
        : null;
    })
    .filter(Boolean);

/** Same mitra picked as both offer and counter on one DU (BE rejects). */
export const getDuplicatePartnerSelections = (
  parcels = [],
  selectionByParcel = {}
) =>
  collectParcelIssues(parcels, selectionByParcel, offers => {
    const seen = new Set();
    const duplicates = new Set();
    offers.forEach(offer => {
      const mitraId = offer?.procurementMitraId ?? offer?.idOffer;
      if (seen.has(mitraId)) duplicates.add(offer.idOffer);
      seen.add(mitraId);
    });
    return duplicates.size ? { idOffers: [...duplicates] } : null;
  });

export const getCounterWithoutIdSelections = (
  parcels = [],
  selectionByParcel = {}
) =>
  collectParcelIssues(parcels, selectionByParcel, offers => {
    const missing = offers.filter(isCounterMissingId);
    return missing.length
      ? { idOffers: missing.map(offer => offer.idOffer) }
      : null;
  });

export const getMissingOfferSelections = (
  parcels = [],
  selectionByParcel = {}
) =>
  (parcels ?? [])
    .map(parcel => {
      const parcelId = getParcelId(parcel);
      if (parcelId == null) return null;
      const entry = selectionByParcel[String(parcelId)];
      if (entry?.requirementIndex == null) return null;
      if (countableOffers(entry?.offers).length > 0) return null;
      return {
        parcelId,
        deliveryUnit: getParcelLabel(parcel),
        requirementIndex: entry.requirementIndex,
      };
    })
    .filter(Boolean);

export const validateNegotiationSelection = (
  parcels = [],
  selectionByParcel = {}
) => {
  const unselectedUnits = getUnselectedDeliveryUnits(
    parcels,
    selectionByParcel
  );
  const missingOffers = getMissingOfferSelections(parcels, selectionByParcel);
  const duplicatePartners = getDuplicatePartnerSelections(
    parcels,
    selectionByParcel
  );
  const counterWithoutId = getCounterWithoutIdSelections(
    parcels,
    selectionByParcel
  );
  const selectedOfferCount = (parcels ?? []).reduce((total, parcel) => {
    const parcelId = getParcelId(parcel);
    if (parcelId == null) return total;
    const offers = countableOffers(selectionByParcel[String(parcelId)]?.offers);
    return total + offers.length;
  }, 0);

  return {
    unselectedUnits,
    missingOffers,
    duplicatePartners,
    counterWithoutId,
    selectedOfferCount,
    isComplete:
      (parcels ?? []).length > 0 &&
      unselectedUnits.length === 0 &&
      missingOffers.length === 0 &&
      duplicatePartners.length === 0 &&
      counterWithoutId.length === 0 &&
      selectedOfferCount > 0,
  };
};

export const formatUnselectedUnitsMessage = units => {
  if (!units?.length) return '';
  const labels = units.map(item => item.deliveryUnit).join(', ');
  return `Masih ada Delivery Unit yang belum dipilih Requirement-nya: ${labels}.`;
};

export const formatMissingOfferMessage = missing => {
  if (!missing?.length) return '';
  return missing
    .map(
      item =>
        `Belum ada Offer dengan ID Offer pada Delivery Unit ${item.deliveryUnit}, Requirement ${item.requirementIndex + 1}`
    )
    .join('; ');
};

export const buildNegotiationSaveSummary = (
  parcels = [],
  selectionByParcel = {},
  procurement
) => {
  const lines = [];

  (parcels ?? []).forEach((parcel, cargoIndex) => {
    const parcelId = getParcelId(parcel);
    if (parcelId == null) return;
    const entry = selectionByParcel[String(parcelId)];
    if (entry?.requirementIndex == null && !entry?.locked) return;

    const cargoLabel = getCargoLabel(parcel, cargoIndex);
    const requirement = buildRequirementOptions(parcel, procurement).find(
      option => option.requirementIndex === entry.requirementIndex
    );
    const offers = countableOffers(entry.offers);
    if (!offers.length) return;

    if (lines.length) lines.push('');
    lines.push(
      `${cargoLabel} · ${requirement?.label ?? `Requirement ${entry.requirementIndex + 1}`}${entry.locked ? ' · Negotiation telah dimulai' : ''}`
    );
    offers.forEach((offer, partnerIndex) => {
      const rowLabel =
        offers.length > 1 ? `${cargoLabel}|P${partnerIndex + 1}` : cargoLabel;
      const kind = offer.kind === 'counter' ? 'Counter Offer · ' : '';
      const company = offer.companyName || offer.sellerName;
      const offerLabel = company
        ? `${company} (${offer.idOffer})`
        : offer.idOffer;
      lines.push(`${rowLabel} — ${kind}${offerLabel}`);
    });
  });

  return lines.join('\n');
};

export const formatNegotiationValidationMessage = validation => {
  if (!validation) return '';
  const parts = [];
  if (validation.unselectedUnits?.length) {
    parts.push(formatUnselectedUnitsMessage(validation.unselectedUnits));
  }
  if (validation.missingOffers?.length) {
    parts.push(formatMissingOfferMessage(validation.missingOffers));
  }
  if (validation.duplicatePartners?.length) {
    parts.push(
      `Satu mitra hanya boleh satu offer/counter per cargo: ${validation.duplicatePartners
        .map(item => `${item.deliveryUnit} (${item.idOffers.join(', ')})`)
        .join('; ')}.`
    );
  }
  if (validation.counterWithoutId?.length) {
    parts.push(
      `Counter belum tersimpan: ${validation.counterWithoutId
        .map(item => `${item.deliveryUnit} (${item.idOffers.join(', ')})`)
        .join('; ')}.`
    );
  }
  if (!parts.length && !(validation.selectedOfferCount > 0)) {
    parts.push(
      'Pilih minimal satu Offer yang punya ID Offer untuk setiap Delivery Unit sebelum menyimpan.'
    );
  }
  return parts.join(' ');
};

const sameId = (a, b) =>
  a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

/**
 * Prefill from `GET /negotiation/{procurementId}`. Returns `selectionByParcel`
 * shaped like wizard state. Cargo already started (statusLabel ≠ NOT_STARTED)
 * is `locked`: shown read-only and resent unchanged (BE rejects edits).
 */
export const buildSelectionFromPackage = (pkg, parcels = [], procurement) => {
  const selectionByParcel = {};
  (pick(pkg, 'cargoes', 'Cargoes') ?? []).forEach(cargo => {
    const termId = pick(
      cargo,
      'quantityDeliveryTermId',
      'QuantityDeliveryTermId'
    );
    const incotermId = pick(cargo, 'quantityIncotermId', 'QuantityIncotermId');
    const parcel = (parcels ?? []).find(item =>
      sameId(getParcelId(item), termId)
    );
    if (!parcel) return;

    const statusLabel = pick(cargo, 'statusLabel', 'StatusLabel');
    const locked = statusLabel
      ? statusLabel !== 'NOT_STARTED'
      : Number(pick(cargo, 'status', 'Status') ?? 0) !== 0;
    const option = buildRequirementOptions(parcel, procurement).find(item =>
      sameId(item.requirement?.quantityIncotermId, incotermId)
    );
    const requirementIndex = option ? option.requirementIndex : null;
    // NOT_STARTED cargo whose requirement no longer exists: let user re-pick.
    if (requirementIndex == null && !locked) return;

    selectionByParcel[String(getParcelId(parcel))] = {
      requirementIndex,
      locked,
      statusLabel: statusLabel ?? null,
      offers: (pick(cargo, 'partners', 'Partners') ?? []).map(partner => ({
        idOffer: pick(partner, 'idOffer', 'IdOffer'),
        procurementMitraId: pick(
          partner,
          'procurementMitraId',
          'ProcurementMitraId'
        ),
        quantityDeliveryTermId: termId,
        quantityIncotermId: incotermId,
        quantityIncotermCounterId: pick(
          partner,
          'quantityIncotermCounterId',
          'QuantityIncotermCounterId'
        ),
        kind: pick(partner, 'kind', 'Kind') ?? 'offer',
        sellerName: pick(partner, 'sellerName', 'SellerName'),
        companyName: pick(partner, 'companyName', 'CompanyName'),
        requirementIndex,
        selectionKey: null,
        rowKey: null,
      })),
    };
  });
  return selectionByParcel;
};
